import * as React from 'react';
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getQuerySuggestions, addQuerySuggestion, deleteQuerySuggestion, updateQuerySuggestion, syncQuerySuggestions } from '@/web/lib/services';
import { type QuerySuggestion, type QuerySuggestionInput } from '@/types';
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen.tsx';
import QuerySuggestionsList from '@/web/components/molecules/QuerySuggestionsList';
import QuerySuggestionFormModal from '../components/molecules/QuerySuggestionFormModal';
import { Button } from '../components/atoms/button';
import { RiLoopRightFill } from '@remixicon/react';

export const Route = createFileRoute('/query-suggestions')({
  loader: async ({ context }) => {
    return context.queryClient?.ensureQueryData(querySuggestionsQueryOptions);
  },
  preload: true,
  component: QuerySuggestions,
  pendingComponent: LoadingScreen
});

const QUERY_SUGGESTIONS_QUERY_KEY = ['query-suggestions'];
const QUERY_SUGGESTIONS_INPUT_DEFAULT = {
  uid: 0,
  suggestion: '',
} as QuerySuggestion;

const querySuggestionsQueryOptions = queryOptions({
  queryKey: QUERY_SUGGESTIONS_QUERY_KEY,
  queryFn: getQuerySuggestions,
  staleTime: 1000 * 60 * 5,
});

function QuerySuggestions() {
  const { error, data } = useSuspenseQuery(querySuggestionsQueryOptions);
  const [isOpen, setIsOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'create' | 'edit'>('create');
  const [querySuggestion, setQuerySuggestion] = React.useState<QuerySuggestion>(QUERY_SUGGESTIONS_INPUT_DEFAULT);

  const queryClient = useQueryClient();

  if(!data) {
    return <div>No data</div>;
  }

  const addQuerySuggestionMutation = useMutation({
    mutationFn: addQuerySuggestion,
    onSuccess: () => {
      queryClient.invalidateQueries(querySuggestionsQueryOptions);
      setIsOpen(false);
    },
  });

  const updateQuerySuggestionMutation = useMutation({
    mutationFn: updateQuerySuggestion,
    onSuccess: () => {
      queryClient.invalidateQueries(querySuggestionsQueryOptions);
      setIsOpen(false);
    },
  });

  const deleteQuerySuggestionMutation = useMutation({
    mutationFn: deleteQuerySuggestion,
    onSuccess: () => {
      queryClient.invalidateQueries(querySuggestionsQueryOptions);
      setIsOpen(false);
    },
  });

  const syncQuerySuggestionsMutation = useMutation({
    mutationFn: syncQuerySuggestions,
    onSuccess: () => {
      queryClient.invalidateQueries(querySuggestionsQueryOptions);
    },
  });

  const handleSubmit = (data: QuerySuggestionInput) => {
    if(mode === 'create') {
      addQuerySuggestionMutation.mutate(data.suggestion);
    } else {
      updateQuerySuggestionMutation.mutate(data);
    }
  };

  const handleDelete = (uid: number) => {
    deleteQuerySuggestionMutation.mutate(uid);
  };

  const handleAddQuerySuggestion = () => {
    setMode('create');
    setQuerySuggestion(QUERY_SUGGESTIONS_INPUT_DEFAULT);
    setIsOpen(true);
  };

  const onEditQuerySuggestion = (querySuggestion: QuerySuggestion) => {
    setMode('edit');
    setQuerySuggestion(querySuggestion);
    setIsOpen(true);
  };

  const handleSyncQuerySuggestions = () => {
    syncQuerySuggestionsMutation.mutate();
  };

  const { suggestions } = data;
  return <div className="main-content mt-24">
    <div className="flex flex-row">
      <div className="flex-1 pl-4">
        <h1 className="text-4xl font-bold">Query Suggestions</h1>
      </div>
      <div className="w-[150px] pr-4">
        <Button onClick={handleSyncQuerySuggestions}>
          <RiLoopRightFill />
        </Button>
        <Button onClick={handleAddQuerySuggestion}>Add Suggestion</Button>
      </div>
    </div>
    <div className="p-4">
      <QuerySuggestionFormModal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        mode={mode}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        querySuggestion={querySuggestion}
      />
      <QuerySuggestionsList suggestions={suggestions} onEditQuerySuggestion={onEditQuerySuggestion} />
    </div>
  </div>;
}
